import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getToken, getUserId } from "../utils/auth";
import "./StaffAssignmentList.css";

export default function StaffAssignmentList() {
  const [assignments, setAssignments] = useState([]);
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const userId = getUserId();

  // Derive college list cleanly from staff data
  const collegeList = useMemo(() => {
    if (!staff) return [];
    if (Array.isArray(staff.colleges) && staff.colleges.length > 0) {
      return staff.colleges;
    }
    return staff.college ? [staff.college] : [];
  }, [staff]);

  useEffect(() => {
    let isMounted = true;

    async function fetchStaffAndAssignments() {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch staff details
        const staffRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/getStaffById/${userId}`,
          {
            headers: { Authorization: `Bearer ${getToken()}` },
          }
        );

        if (!staffRes.ok) throw new Error("Failed to load staff metadata.");
        const staffData = await staffRes.json();

        if (!isMounted) return;
        setStaff(staffData);

        const targetColleges =
          Array.isArray(staffData.colleges) && staffData.colleges.length > 0
            ? staffData.colleges
            : staffData.college
            ? [staffData.college]
            : [];

        if (targetColleges.length === 0) {
          setAssignments([]);
          return;
        }

        // 2. Fetch assignments across colleges concurrently
        const fetchPromises = targetColleges.map(async (collegeName) => {
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/getDistinctAssignmentsByCollege`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ college: collegeName }),
              }
            );
            if (!res.ok) return null;
            return await res.json();
          } catch (err) {
            console.error(`Error fetching for college: ${collegeName}`, err);
            return null;
          }
        });

        const results = await Promise.all(fetchPromises);

        // 3. Deduplicate assignments using Map
        const assignmentMap = new Map();
        results.forEach((assignData) => {
          if (assignData?.data && Array.isArray(assignData.data)) {
            assignData.data.forEach((item) => {
              const details = item.assignmentDetails;
              if (details?._id) {
                assignmentMap.set(details._id, details);
              }
            });
          }
        });

        if (isMounted) {
          setAssignments(Array.from(assignmentMap.values()));
        }
      } catch (err) {
        console.error("Error fetching assignments:", err);
        if (isMounted) setError("Unable to load assignments. Please try again later.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if (userId) {
      fetchStaffAndAssignments();
    } else {
      setLoading(false);
      setError("User session not found.");
    }

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleCardClick = (assignmentId) => {
    navigate(`/staff/assignment-report`, {
      state: {
        colleges: collegeList,
        college: staff?.college,
        assignmentId,
      },
    });
  };

  // Loading State
  if (loading) {
    return (
      <div className="state-container">
        <div className="loading-spinner" aria-label="Loading indicator" />
        <p className="state-text">Loading assignments dashboard...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="state-container">
        <div className="error-card">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button className="btn-secondary" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assignment-page">
      <header className="page-header">
        <div className="header-content">
          <h1 className="page-title">Assignments Dashboard</h1>
          {/* <p className="page-subtitle">
            Monitor activity and analyze student engagement across assigned cohorts.
          </p> */}
        </div>
        {collegeList.length > 0 && (
          <div className="header-meta">
            <span className="scope-badge">
              {collegeList.length === 1 ? collegeList[0] : `${collegeList.length} Colleges`}
            </span>
          </div>
        )}
      </header>

      {assignments.length === 0 ? (
        <div className="empty-state">
          <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3>No Assignments Found</h3>
          <p>There are currently no active assignments associated with your department or assigned institutions.</p>
        </div>
      ) : (
        <div className="assignment-grid">
          {assignments.map((assignment) => {
            const dueDateFormatted = assignment.dueDate
              ? new Date(assignment.dueDate).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "N/A";

            const questionCount = assignment.questions?.length || 0;

            return (
              <article
                key={assignment._id}
                className="assignment-card"
                tabIndex={0}
                role="button"
                onClick={() => handleCardClick(assignment._id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCardClick(assignment._id);
                  }
                }}
              >
                <div className="card-header">
                  <h2 className="card-title">{assignment.name}</h2>
                  <span className="badge badge-active">Active</span>
                </div>

                <p className="description">
                  {assignment.description || "No description provided for this assignment."}
                </p>

                <div className="meta-container">
                  <div className="meta-item">
                    <span className="meta-label">Due Date</span>
                    <span className="meta-value">{dueDateFormatted}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Assessment</span>
                    <span className="meta-value">{questionCount} {questionCount === 1 ? "Question" : "Questions"}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <span className="difficulty-tag easy">Easy</span>
                  <span className="action-link">
                    View Performance Report
                    <span className="arrow" aria-hidden="true"> →</span>
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}